import { useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useChainlinkDesigns } from "../../../hooks/useChainlinkDesigns";
import { useTeam } from "../../../hooks/useTeam";
import { api } from "../../../util/api";
import { useSnackbar } from "notistack";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ImageIcon from "@mui/icons-material/Image";
import TextFieldTW from "../../../comp/tw/TextField";
import { ButtonTW } from "../../../comp/tw/Button";
import { ModalTW } from "../../../comp/tw/Modal";

const DesignRow = ({ design, dispatch, setActiveTab, designId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutate, chainlinkDesigns } = useChainlinkDesigns();

  const { team } = useTeam();
  const [areYouSureImage, setAreYouSureImage] = useState(false);
  const [areYouSureDesign, setAreYouSureDesign] = useState(false);

  const isPublicCheckbox = async (e) => {
    enqueueSnackbar("Saving Design Privacy");
    await api("PUT", `/team/chainlinkDesign/${design.id}`, {
      isPublic: e.target.checked,
    });

    mutate(
      chainlinkDesigns.map((d) =>
        d.id === design.id ? { ...d, isPublic: e.target.checked } : d,
      ),
    );
    enqueueSnackbar("Design Privacy Updated", { variant: "success" });
  };

  const saveButton = async (values, actions) => {
    enqueueSnackbar("Saving Design Name");
    await api("PUT", `/team/chainlinkDesign/${design.id}`, values);
    mutate(
      chainlinkDesigns.map((d) =>
        d.id === design.id ? { ...d, ...values } : d,
      ),
    );
    enqueueSnackbar("Design Name Updated", { variant: "success" });
  };

  const handleImageChange = (e) => {
    enqueueSnackbar("Saving Design Image");

    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        const aspectRatio = img.width / img.height;
        const maxWidth = 2000;
        const maxHeight = 2000;

        let targetWidth;
        let targetHeight;

        if (img.width > maxWidth || img.height > maxHeight) {
          if (img.width > img.height) {
            targetWidth = maxWidth;
            targetHeight = maxWidth / aspectRatio;
          } else {
            targetHeight = maxHeight;
            targetWidth = maxHeight * aspectRatio;
          }
        } else {
          targetWidth = img.width;
          targetHeight = img.height;
        }

        createImageBitmap(img, {
          resizeWidth: targetWidth,
          resizeHeight: targetHeight,
        }).then(async (resizedBitmap) => {
          let quality = 0.99;
          let canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(resizedBitmap, 0, 0, targetWidth, targetHeight);
          let dataUrl = canvas.toDataURL("image/jpeg", quality);

          while (dataUrl.length > 1000000) {
            quality -= 0.01;
            dataUrl = canvas.toDataURL("image/jpeg", quality);
          }

          await api("PUT", `/team/chainlinkDesign/${designId}`, {
            image: dataUrl,
          });
          mutate(
            chainlinkDesigns.map((d) =>
              d.id === designId ? { ...d, image: dataUrl } : d,
            ),
            { revalidate: false },
          );
          enqueueSnackbar("Design Image Updated", { variant: "success" });
        });
      };

      img.src = event.target.result;
    };

    reader.readAsDataURL(file);
  };

  const areYouSureButtonDeleteDesign = async () => {
    try {
      enqueueSnackbar("Deleting Design");

      await api("DELETE", `/team/chainlinkDesign/${design.id}`);
      mutate(chainlinkDesigns.filter((d) => d.id !== design.id));
      enqueueSnackbar("Deleted", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e.response.data, { variant: "error" });
    }
    setAreYouSureImage(false);
  };

  const areYouSureButtonDeleteImage = async (values, actions) => {
    try {
      enqueueSnackbar("Deleting Image");
      const newDesigns = chainlinkDesigns.map((d) =>
        d.id === design.id ? { ...d, image: null } : d,
      );

      await api("PUT", `/team/chainlinkDesign/${design.id}`, { image: null });
      mutate(newDesigns);
      enqueueSnackbar("Deleted", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e.response.data, { variant: "error" });
    }
    setAreYouSureImage(false);
  };

  const deleteImageButton = () => setAreYouSureImage(true);

  const deleteDesignButton = () => setAreYouSureDesign(true);

  const copyToTable = () => {
    dispatch({ type: "copyDesign", payload: design });
    setActiveTab(2); // Switch to Build tab
    enqueueSnackbar("Copied to Build tab", { variant: "success" });
  };

  return (
    <div>
      <Formik
        initialValues={{
          name: design.name,
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string()
            .min(2, "Too Short!")
            .max(50, "Too Long!")
            .required("Required"),
        })}
        onSubmit={saveButton}
      >
        {({ submitForm, isSubmitting, setFieldValue, values }) => (
          <Form>
            <div className="grid grid-cols-5 gap-x-1 content-center my-4 border-solid border-zinc-300 rounded-lg border-4 p-2 items-center">
              <div className="col-span-5 text-center">
                <TextFieldTW name="name" label="Design Name:" />

                {team.tierLimits.publicPages &&
                team.permissions.includes("editPublic") ? (
                  <div className="col-span-5 text-center my-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        className="form-checkbox h-5 w-5 text-indigo-600"
                        onChange={isPublicCheckbox}
                        checked={design.isPublic ? true : false}
                      />

                      <span className="ml-2 text-gray-700">
                        {design.isPublic ? "Make Private" : "Public"}
                      </span>
                    </label>
                  </div>
                ) : null}
              </div>
              {design.image ? (
                <div className="col-span-5 flex justify-center">
                  <img
                    alt="design"
                    src={design.image}
                    className="my-1 min-h-48 max-h-48"
                  />
                </div>
              ) : (
                <div className="my-1 min-h-48 col-span-5 flex justify-center">
                  <p> No Image</p>
                </div>
              )}
              {isSubmitting ? (
                <div className="spinner" />
              ) : (
                <ButtonTW
                  variant="primary"
                  disabled={isSubmitting || values.name === design.name}
                  onClick={submitForm}
                  label={<SaveIcon />}
                />
              )}
              <ButtonTW
                variant="primary"
                onClick={copyToTable}
                label={<ContentCopyIcon />}
              />
              <div className="transform hover:scale-105 hover:shadow-2xl px-3 py-2 inline-flex items-center justify-center font-semibold transition ease-in-out duration-200 rounded focus:outline-none focus:ring-4 text-white bg-gradient-to-r from-indigo-500 via-blue-500 to-indigo-500 hover:from-indigo-500 hover:via-purple-500 hover:to-indigo-500 shadow-lg shadow-indigo-500/50 focus:ring-indigo-400">
                <label htmlFor={`designUpload - ${designId}`}>
                  <ImageIcon />
                </label>

                <input
                  type="file"
                  accept="image/*"
                  id={`designUpload - ${designId}`}
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>

              {design.image ? (
                <ButtonTW
                  variant="danger"
                  label={<ImageIcon />}
                  onClick={deleteImageButton}
                />
              ) : null}

              <ModalTW
                isOpen={areYouSureImage}
                text="Are you sure you want to delete this image?"
                confirm={areYouSureButtonDeleteImage}
                onClose={() => setAreYouSureImage(false)}
              />

              <ButtonTW
                variant="danger"
                label={<DeleteIcon />}
                onClick={deleteDesignButton}
              />

              <ModalTW
                isOpen={areYouSureDesign}
                text="Are you sure you want to delete this design?"
                confirm={areYouSureButtonDeleteDesign}
                onClose={() => setAreYouSureDesign(false)}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const ChainlinkDesigns = ({ dispatch, setActiveTab }) => {
  const { chainlinkDesigns, isLoading, error } = useChainlinkDesigns();
  const { team, isLoading: teamIsLoading, error: teamError } = useTeam();

  if (isLoading || teamIsLoading || !chainlinkDesigns) {
    return <div className="spinner" />;
  }

  if (error || teamError) {
    return (
      <p>
        Error! please refresh the page, if the problem persists contact
        fencewizapp@gmail.com
      </p>
    );
  }

  return (
    <div>
      <h2 className="text-xl mb-5">{team.team.name} Chain Link Designs</h2>
      {team.tierLimits.designs <= chainlinkDesigns.length && (
        <p>No More Designs Allowed</p>
      )}
      {chainlinkDesigns.length === 0 ? <p>No Designs Yet</p> : null}

      <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-x-4">
        {chainlinkDesigns && chainlinkDesigns.length
          ? chainlinkDesigns
              .sort((a, b) => (a.name < b.name ? -1 : 1))
              .map((design) => (
                <DesignRow
                  key={design.id}
                  design={design}
                  dispatch={dispatch}
                  setActiveTab={setActiveTab}
                  designId={design.id}
                />
              ))
          : null}
      </div>
    </div>
  );
};

export default ChainlinkDesigns;
